import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const tempQuery = state.pages?.productList?.filteredApiParameter?.searchWithin;
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        tempQuery,
        searchQuery: state.pages.productList.parameter?.search,
        productsDataView: getProductListDataView(state),
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
            display: flex;
            justify-content: flex-start;
            padding-left: 22px;
            font-size: 18px;
            letter-spacing: 0;
            line-height: 22px;
            color: #33333a;
            flex-wrap: wrap;
            @media all and (max-width: 992px) and (min-width: 0px) {
                margin-top: 0px;
                justify-content: start;
                padding-left: 0px;
            }
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
            margin-right: 5px;
            font-weight: 600;
        `,
    },
    queryText: {
        css: css`
            text-transform: capitalize;
            margin-left: 3px;
            font-weight: 700;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, tempQuery, searchQuery, productsDataView }) => {
    if (!totalItemCount) {
        return null;
    }

    const displayView = () => {
        let temp;
        if (query) {
            temp = query;
        } else {
            temp = tempQuery;
        }
        const itemsText = (totalItemCount !== 1 ? translate("items") : translate("item")) + (temp ? ` ${translate("for")}` : " ");
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.countText} data-test-selector="productListCountText">
                    {totalItemCount}
                </Typography>
                <Typography {...styles.itemsText}>{itemsText}</Typography>
                {temp && (
                    <Typography {...styles.queryText} data-test-selector="productListQueryText">
                        {temp}
                    </Typography>
                )}
            </StyledWrapper>
        );
    };

    const conditionalView = () => {
        if (tempQuery) {
            return displayView();
        }
        if (searchQuery && productsDataView.value?.length === 0) {
            return null;
        }
        return displayView();
    };

    return conditionalView();
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
